import { Patient } from "@clients/velys-infrastructure/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getPatientById,
  getPatients,
  updatePatient,
  updatePatientToS,
} from "../dataAccess/apiService";
import {
  usersGql,
  GET_PATIENT_PREFERENCES,
  UPDATE_PATIENT_PREFERENCES,
  GetPatientPreferencesResponse,
  UpdatePatientPreferencesResponse,
  PatientPreferencesInput,
} from "@/lib/dataAccess/appsync";
import { useAuth } from "react-oidc-context";
import { useAuthStore } from "../stores/useAuthStore";

//create patient enrollment
export const useGetPatients = () => {
  const auth = useAuth();

  const patientsQuery = useQuery({
    queryKey: ["get-patients"],
    queryFn: () => {
      return getPatients();
    },
    enabled: !!auth.isAuthenticated,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });

  return patientsQuery;
};

export const useGetPatientById = (patientId?: string) => {
  const patientQuery = useQuery({
    queryKey: ["get-patient", patientId],
    queryFn: () => {
      if (!patientId) {
        return Promise.reject(new Error("Missing patientId"));
      }
      return getPatientById(patientId);
    },
    enabled: !!patientId,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  return patientQuery;
};

export const useUpdatePatient = () => {
  const queryClient = useQueryClient();

  const updatePatientMutation = useMutation({
    mutationFn: async ({
      patient,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      isUpdate,
    }: {
      patient: Patient;
      isUpdate: boolean;
    }) => {
      return await updatePatient(patient);
    },
    onSuccess: (data, variables) => {
      //refetch
      if (variables.isUpdate) {
        queryClient.invalidateQueries({
          queryKey: ["get-patient", data.patient_id],
        });
      }
    },
    onError: (error, variables) => {
      console.error("Create/Update Patient Error: ", { error, variables });
    },
  });

  return updatePatientMutation;
};

export const useUpdatePatientToS = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      patientId,
      patient,
    }: {
      patientId: string;
      patient: Patient;
    }) => {
      return await updatePatientToS(patientId, patient);
    },
    onSuccess: (data, { patientId }) => {
      queryClient.invalidateQueries({ queryKey: ["get-patient", patientId] });
    },
    onError: (error) => {
      console.error("Failed to update patient ToS:", error);
    },
  });
};

// Patient notification preferences (VELYS-518). Self-service only — no
// userId argument on either operation; the caller is always the target,
// enforced server-side (a separate authorization path from
// getUser/updateUser's admin-or-org-member check — deliberately not reused
// here). GraphQL via appsync, not the REST apiService used above.
export const usePatientPreferences = () => {
  const user_token = useAuthStore((s) => s.user_token);

  return useQuery({
    queryKey: ["patient-preferences"],
    queryFn: () =>
      usersGql<GetPatientPreferencesResponse>(
        user_token!,
        GET_PATIENT_PREFERENCES
      ),
    enabled: !!user_token,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });
};

export const useUpdatePatientPreferences = () => {
  const user_token = useAuthStore((s) => s.user_token);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (preferences: PatientPreferencesInput) =>
      usersGql<UpdatePatientPreferencesResponse>(
        user_token!,
        UPDATE_PATIENT_PREFERENCES,
        { preferences }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["patient-preferences"] });
    },
  });
};
